import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, TrendingUp, ArrowRight, Hash, Zap, Loader2 } from 'lucide-react';
import { getPublicTopics } from '../../lib/apiClient';

export default function TopicIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load Trending Once
  useEffect(() => {
      async function loadTrending() {
          try {
              const res = await getPublicTopics({ sort: 'trending', limit: 3 });
              setTrendingTopics(res || []);
          } catch (e) {
              console.error('Failed to load trending', e);
          }
      }
      loadTrending();
  }, []);

  // Load All / Search
  useEffect(() => {
    async function loadTopics() {
        try {
            setLoading(true);
            // If query exists, api filters by it. If not, returns recent.
            const res = await getPublicTopics({ q: query }); 
            setAllTopics(res || []);
        } catch (e) {
            console.error('Failed to load topics', e);
        } finally {
            setLoading(false);
        }
    }
    
    // Simple debounce could go here, but for now direct call
    const timer = setTimeout(() => loadTopics(), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(query ? { q: query } : {});
  };

  return (
    <div className="min-h-screen bg-background text-white font-sans pb-20">
      {/* Page Header */}
      <div className="relative pt-20 pb-16 px-6 border-b border-white/5 bg-gradient-to-b from-surface to-background">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-full bg-blue-500/5 blur-3xl pointer-events-none" />
        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">Explore Topics</h1>
          <p className="text-lg text-slate-400 mb-8">Ideas extracted from the world’s most insightful podcast conversations.</p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto group">
            <input 
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search ideas, not episodes..." 
              className="w-full bg-surface/80 border border-white/10 rounded-full py-4 pl-14 pr-6 text-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 focus:bg-surface transition-all shadow-lg"
            />
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-500 group-focus-within:text-primary transition-colors" />
          </form>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-12">
        
        {/* Trending Section (Only show if no search). Filter out topics with zero insights. */}
        {!query && trendingTopics.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-secondary" />
              <h2 className="text-xl font-bold text-white">Trending Now</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {trendingTopics
                .filter(topic => (topic.recent_insight_count ?? topic.insight_count ?? 0) > 0)
                .map(topic => (
                <Link key={topic.id} to={`/topics/${topic.slug}`} className="block group">
                  <div className="h-full p-6 rounded-2xl bg-surface border border-white/5 hover:border-secondary/50 hover:shadow-glow-purple transition-all relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-2 group-hover:translate-x-0">
                      <ArrowRight className="w-5 h-5 text-secondary" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-secondary transition-colors">{topic.title}</h3>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">{topic.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-auto">
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-yellow-500" /> {topic.recent_insight_count || 'New'} insights</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Topics Grid */}
        <div>
          <h2 className="text-xl font-bold text-white mb-6">{query ? `Results for "${query}"` : 'All Topics'}</h2>
          
          {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary"/></div>
          ) : allTopics.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allTopics
                .filter(topic => (topic.recent_insight_count ?? topic.insight_count ?? topic.hero_summary_insight_count ?? 0) > 0)
                .map(topic => (
                <Link key={topic.id} to={`/topics/${topic.slug}`} className="block group">
                  <div className="p-6 rounded-xl bg-surface/50 border border-white/5 hover:border-primary/30 hover:bg-surface transition-all h-full flex flex-col">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-bold text-lg text-white group-hover:text-primary transition-colors">{topic.title}</h3>
                    </div>
                    <p className="text-sm text-slate-400 mb-4 line-clamp-2 flex-1">{topic.description}</p>
                    {/* Tags aren't in standard API response yet, hiding or using keywords if available */}
                    {topic.keywords && topic.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                        {topic.keywords.slice(0,3).map(tag => (
                            <span key={tag} className="px-2 py-1 rounded-md bg-white/5 text-xs text-slate-400 border border-white/5">
                            {tag}
                            </span>
                        ))}
                        </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-slate-500 border-t border-white/5 pt-4 mt-auto">
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-500" /> 
                        {topic.hero_summary_insight_count || topic.insight_count || 0} insights
                      </span>
                      <span className="text-primary opacity-0 group-hover:opacity-100 transition-opacity font-medium">Explore →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-surface/30 rounded-2xl border border-white/5">
              <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No topics found</h3>
              <p className="text-slate-400 max-w-md mx-auto">
                We don't have a topic hub for "{query}" yet. Try searching for something else.
              </p>
              <button onClick={() => {setQuery(''); setSearchParams({});}} className="mt-6 text-primary hover:text-primary/80 font-medium">
                Clear search
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
